import React, { useState } from 'react';
import { RIPPLE_ON_LIGHT } from '../../globalStyles';
import { ContactError, sendContactMessage } from '../../contact/client';
import { CONTACT_EMAIL } from '../../contact/config';
import ReflectionButton from '../reflectionButton/reflectionButton';
import { styles } from './styles';

type Status = 'idle' | 'sending' | 'sent' | 'error';

type Field = 'name' | 'email' | 'topic' | 'subject' | 'message';

type TextField = 'name' | 'email' | 'subject' | 'message' | 'contactExtra';

const EMPTY = { name: '', email: '', topic: '', subject: '', message: '', contactExtra: '' };

const ORDER: Field[] = ['name', 'email', 'topic', 'subject', 'message'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (form: typeof EMPTY, hasTopics: boolean): Partial<Record<Field, string>> => {

  const errors: Partial<Record<Field, string>> = {};

  if (!form.name.trim()) {
    errors.name = 'Please enter your name.';
  }
  if (!form.email.trim()) {
    errors.email = 'Please enter your email.';
  }
  else if (!EMAIL_RE.test(form.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }
  if (hasTopics && !form.topic) {
    errors.topic = 'Please choose a topic.';
  }
  if (!hasTopics && !form.subject.trim()) {
    errors.subject = 'Please add a subject.';
  }
  if (!form.message.trim()) {
    errors.message = 'Please enter a message.';
  }

  return errors;
};

export default function ContactForm(props: {
  idPrefix: string;
  variant: 'novaeon' | 'playercafe';
  emailPlaceholder: string;
  messagePlaceholder: string;
  topics?: readonly string[];
  subjectPrefix?: string;
}): JSX.Element {

  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Field, string>>>({});
  const hasTopics = Boolean(props.topics && props.topics.length > 0);
  const sending = status === 'sending';
  const sent = status === 'sent';
  const locked = sending || sent;
  const fieldId = (field: string): string => `${props.idPrefix}-${field}`;

  const clearFeedback = (field: Field | 'contactExtra'): void => {
    if (field !== 'contactExtra' && fieldErrors[field]) {
      setFieldErrors((previous) => ({ ...previous, [field]: undefined }));
    }
    if (status === 'error') {
      setStatus('idle');
      setError('');
    }
  };

  const set = (field: TextField) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {

    const value = event.target.value;

    setForm((previous) => ({ ...previous, [field]: value }));
    clearFeedback(field);
  };

  const chooseTopic = (topic: string): void => {
    setForm((previous) => ({ ...previous, topic: topic }));
    clearFeedback('topic');
  };

  const fieldStyle = (field: Field): React.CSSProperties => fieldErrors[field]
    ? { ...styles.input, ...styles.fieldInvalid }
    : styles.input;

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {

    event.preventDefault();

    if (locked) {
      return;
    }

    const errors = validate(form, hasTopics);

    if (Object.keys(errors).length > 0) {

      const firstInvalid = ORDER.find((field) => errors[field]);

      setFieldErrors(errors);
      setStatus('idle');
      setError('');
      if (firstInvalid) {
        document.getElementById(fieldId(firstInvalid))?.focus();
      }

      return;
    }

    setFieldErrors({});
    setStatus('sending');
    setError('');

    try {
      await sendContactMessage({
        name: form.name,
        email: form.email,
        subject: hasTopics ? `${props.subjectPrefix ?? ''} ${form.topic}`.trim() : form.subject,
        message: form.message,
        contactExtra: form.contactExtra,
      });
      setStatus('sent');
      setForm(EMPTY);
    }
    catch (caught) {

      const contactError = caught as ContactError;

      setError(contactError.code === 'unconfigured'
        ? `Please email us directly at ${CONTACT_EMAIL}.`
        : contactError.message || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  return (
    <form style={styles.form} onSubmit={handleSubmit} noValidate>
      <div className="contact-formrow" style={styles.formRow}>
        <div style={styles.field}>
          <label style={styles.label} htmlFor={fieldId('name')}>Name</label>
          <input
            id={fieldId('name')}
            name="name"
            style={fieldStyle('name')}
            type="text"
            placeholder="Your name"
            value={form.name}
            onChange={set('name')}
            aria-invalid={!!fieldErrors.name}
            disabled={locked}
          />
          {fieldErrors.name && <span style={styles.fieldErrorText}>{fieldErrors.name}</span>}
        </div>
        <div style={styles.field}>
          <label style={styles.label} htmlFor={fieldId('email')}>Email</label>
          <input
            id={fieldId('email')}
            name="email"
            style={fieldStyle('email')}
            type="email"
            placeholder={props.emailPlaceholder}
            value={form.email}
            onChange={set('email')}
            aria-invalid={!!fieldErrors.email}
            disabled={locked}
          />
          {fieldErrors.email && <span style={styles.fieldErrorText}>{fieldErrors.email}</span>}
        </div>
      </div>

      {hasTopics ? (
        <div style={styles.field}>
          <span style={styles.label} id={fieldId('topic-label')}>Topic</span>
          <div
            role="radiogroup"
            aria-labelledby={fieldId('topic-label')}
            style={fieldErrors.topic ? { ...styles.topics, ...styles.topicsInvalid } : styles.topics}
          >
            {props.topics?.map((topic, index) => (
              <button
                key={topic}
                id={index === 0 ? fieldId('topic') : undefined}
                type="button"
                role="radio"
                aria-checked={form.topic === topic}
                style={form.topic === topic ? styles.topicSelected : styles.topic}
                onClick={() => chooseTopic(topic)}
                disabled={locked}
              >
                {topic}
              </button>
            ))}
          </div>
          {fieldErrors.topic && <span style={styles.fieldErrorText}>{fieldErrors.topic}</span>}
        </div>
      ) : (
        <div style={styles.field}>
          <label style={styles.label} htmlFor={fieldId('subject')}>Subject</label>
          <input
            id={fieldId('subject')}
            name="subject"
            style={fieldStyle('subject')}
            type="text"
            placeholder="What can we help with?"
            value={form.subject}
            onChange={set('subject')}
            aria-invalid={!!fieldErrors.subject}
            disabled={locked}
          />
          {fieldErrors.subject && <span style={styles.fieldErrorText}>{fieldErrors.subject}</span>}
        </div>
      )}

      <div style={styles.field}>
        <label style={styles.label} htmlFor={fieldId('message')}>Message</label>
        <textarea
          id={fieldId('message')}
          name="message"
          style={fieldErrors.message ? { ...styles.textarea, ...styles.fieldInvalid } : styles.textarea}
          rows={5}
          placeholder={props.messagePlaceholder}
          value={form.message}
          onChange={set('message')}
          aria-invalid={!!fieldErrors.message}
          disabled={locked}
        />
        {fieldErrors.message && <span style={styles.fieldErrorText}>{fieldErrors.message}</span>}
      </div>

      <div style={styles.honeypot} aria-hidden="true">
        <label htmlFor={fieldId('extra')}>Leave this field empty</label>
        <input
          id={fieldId('extra')}
          name="contactExtra"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.contactExtra}
          onChange={set('contactExtra')}
        />
      </div>

      <ReflectionButton
        type="submit"
        style={{ ...(props.variant === 'playercafe' ? styles.submitPlayerCafe : styles.submitNovaeon), opacity: locked ? 0.6 : 1 }}
        highlightColor={RIPPLE_ON_LIGHT}
        disabled={locked}
      >
        {sending ? 'Sending…' : sent ? 'Message sent' : 'Send message'}
      </ReflectionButton>

      {status === 'error' && <p style={{ ...styles.feedback, ...styles.feedbackError }} role="alert">{error}</p>}
      {sent && (
        <p style={{ ...styles.feedback, ...styles.feedbackSuccess }} role="status">
          Thanks, we'll be in touch within one business day.
        </p>
      )}
    </form>
  );

}
